export const ORCA_ELICITATION_SCHEMA = "orca.elicitation/v1" as const
export const ORCA_ELICITATION_RESPONSE_SCHEMA = "orca.elicitation-response/v1" as const

export const ORCA_ELICITATION_HANDLER_IDS = [
  "project_fact.lot_size.resolve",
] as const

export type OrcaElicitationHandlerId = typeof ORCA_ELICITATION_HANDLER_IDS[number]
export type OrcaElicitationOrigin = "directive" | "agent"
export type OrcaElicitationStatus = "pending" | "answered" | "expired" | "superseded"

export interface OrcaElicitationOptionV1 {
  id: string
  label: string
  description?: string
  sourceLabels?: string[]
  isCurrent?: boolean
}

export interface OrcaElicitationNumberFieldV1 {
  id: string
  kind: "number"
  label: string
  description?: string
  placeholder?: string
  unit?: string
  min?: number
  max?: number
  step?: number
}

export interface OrcaElicitationTextFieldV1 {
  id: string
  kind: "text"
  label: string
  description?: string
  placeholder?: string
  minLength?: number
  maxLength?: number
}

export type OrcaElicitationFieldV1 =
  | OrcaElicitationNumberFieldV1
  | OrcaElicitationTextFieldV1

export interface OrcaElicitationSingleChoiceControlV1 {
  id: string
  kind: "single_choice"
  label: string
  options: OrcaElicitationOptionV1[]
  other?: {
    label: string
    description?: string
    field: OrcaElicitationFieldV1
  }
}

export interface OrcaElicitationFieldsControlV1 {
  id: string
  kind: "fields"
  label: string
  fields: OrcaElicitationFieldV1[]
}

export interface OrcaElicitationDocumentControlV1 {
  id: string
  kind: "document"
  label: string
  description?: string
  allowedMimeTypes: string[]
  maxFiles: number
}

export interface OrcaElicitationApprovalControlV1 {
  id: string
  kind: "approval"
  label: string
  description?: string
  approveLabel: string
  rejectLabel: string
}

export type OrcaElicitationControlV1 =
  | OrcaElicitationSingleChoiceControlV1
  | OrcaElicitationFieldsControlV1
  | OrcaElicitationDocumentControlV1
  | OrcaElicitationApprovalControlV1

export interface OrcaElicitationAlternativeV1 {
  id: string
  kind: "research" | "activate_agent" | "ask_orca"
  label: string
  description?: string
  credits?: number
  requiresApproval: boolean
}

export interface OrcaElicitationSpecV1 {
  schema: typeof ORCA_ELICITATION_SCHEMA
  requestId: string
  title: string
  description: string
  controls: OrcaElicitationControlV1[]
  alternatives?: OrcaElicitationAlternativeV1[]
  submitLabel: string
  cancelLabel?: string
  notice?: string
  cost: {
    credits: number
    requiresApproval: boolean
  }
  sourceRevision: string
}

export type OrcaElicitationAnswerV1 =
  | { kind: "option"; controlId: string; optionId: string }
  | { kind: "fields"; controlId: string; fields: Record<string, string | number | boolean> }
  | { kind: "other"; controlId: string; fieldId: string; value: string | number | boolean }
  | { kind: "approval"; controlId: string; approved: boolean }
  | { kind: "alternative"; alternativeId: string }

export interface OrcaElicitationResponseV1 {
  schema: typeof ORCA_ELICITATION_RESPONSE_SCHEMA
  requestId: string
  idempotencyKey: string
  answer: OrcaElicitationAnswerV1
}

export interface OrcaElicitationSourceState {
  revision: string
  candidateSetKey?: string
  projectLocationIdentity?: string
}

export interface OrcaElicitationAgentLineage {
  conversationId: string
  sessionId: string
  functionCallId: string
}

export interface OrcaElicitationRequestDocument {
  schema: typeof ORCA_ELICITATION_SCHEMA
  requestId: string
  projectId: string
  ownerId: string
  origin: OrcaElicitationOrigin
  handlerId: OrcaElicitationHandlerId
  status: OrcaElicitationStatus
  spec: OrcaElicitationSpecV1
  specHash: string
  sourceState: OrcaElicitationSourceState
  attentionId?: string
  agentLineage?: OrcaElicitationAgentLineage
  createdAt: unknown
  updatedAt: unknown
  expiresAt?: unknown
  answeredAt?: unknown
  answeredBy?: string
  response?: OrcaElicitationResponseV1
  responseHash?: string
  result?: Record<string, unknown>
}

/** Owner-filtered request projection returned to the browser. */
export interface ReadableOrcaElicitationRequest {
  schema: typeof ORCA_ELICITATION_SCHEMA
  requestId: string
  origin: OrcaElicitationOrigin
  handlerId: OrcaElicitationHandlerId
  status: Extract<OrcaElicitationStatus, "pending" | "answered">
  spec: OrcaElicitationSpecV1
  createdAt: unknown
  updatedAt: unknown
  expiresAt?: unknown
  answeredAt?: unknown
  result?: Record<string, unknown>
  continuationState?: ReadableAgentElicitationContinuationState
}

export type AgentElicitationContinuationState =
  | "awaiting_response"
  | "awaiting_continuation"

export type ReadableAgentElicitationContinuationState =
  | AgentElicitationContinuationState
  | "resumed"

export interface RecoverableAgentElicitationCheckpoint {
  requestId: string
  origin: "agent"
  handlerId: OrcaElicitationHandlerId
  status: "pending" | "answered"
  continuationState: AgentElicitationContinuationState
  spec: OrcaElicitationSpecV1
  result?: Record<string, unknown>
  createdAt: unknown
  updatedAt: unknown
  answeredAt?: unknown
}

const ID_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9._:-]*$/

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value)
}

function hasOnlyKeys(value: Record<string, unknown>, keys: readonly string[]): boolean {
  const allowed = new Set(keys)
  return Object.keys(value).every((key) => allowed.has(key))
}

function boundedText(value: unknown, max: number): string | null {
  if (typeof value !== "string") return null
  const normalized = value.trim()
  return normalized.length > 0 && normalized.length <= max ? normalized : null
}

function optionalTrimmedText(value: unknown, max: number): string | undefined | null {
  if (value === undefined) return undefined
  if (typeof value !== "string") return null
  const normalized = value.trim()
  return normalized.length <= max ? normalized : null
}

function boundedId(value: unknown): string | null {
  const normalized = boundedText(value, 160)
  return normalized && ID_PATTERN.test(normalized) ? normalized : null
}

function finiteNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null
}

function boundedInteger(value: unknown, min: number, max: number): number | null {
  return typeof value === "number"
    && Number.isInteger(value)
    && value >= min
    && value <= max
    ? value
    : null
}

function parseOption(value: unknown): OrcaElicitationOptionV1 | null {
  if (!isRecord(value) || !hasOnlyKeys(value, [
    "id", "label", "description", "sourceLabels", "isCurrent",
  ])) return null
  const id = boundedId(value.id)
  const label = boundedText(value.label, 120)
  const description = value.description === undefined
    ? undefined
    : boundedText(value.description, 300)
  if (!id || !label || description === null) return null
  let isCurrent: boolean | undefined
  if (value.isCurrent !== undefined) {
    if (typeof value.isCurrent !== "boolean") return null
    isCurrent = value.isCurrent
  }

  let sourceLabels: string[] | undefined
  if (value.sourceLabels !== undefined) {
    if (!Array.isArray(value.sourceLabels) || value.sourceLabels.length > 6) return null
    sourceLabels = []
    for (const sourceLabel of value.sourceLabels) {
      const parsed = boundedText(sourceLabel, 120)
      if (!parsed) return null
      sourceLabels.push(parsed)
    }
  }

  return {
    id,
    label,
    ...(description ? { description } : {}),
    ...(sourceLabels ? { sourceLabels } : {}),
    ...(isCurrent !== undefined ? { isCurrent } : {}),
  }
}

function parseField(value: unknown): OrcaElicitationFieldV1 | null {
  if (!isRecord(value)) return null
  const id = boundedId(value.id)
  const label = boundedText(value.label, 120)
  const description = value.description === undefined
    ? undefined
    : boundedText(value.description, 300)
  if (!id || !label || description === null) return null

  if (value.kind === "number") {
    if (!hasOnlyKeys(value, [
      "id", "kind", "label", "description", "placeholder", "unit", "min", "max", "step",
    ])) return null
    const placeholder = optionalTrimmedText(value.placeholder, 40)
    const unit = optionalTrimmedText(value.unit, 24)
    const min = value.min === undefined ? undefined : finiteNumber(value.min)
    const max = value.max === undefined ? undefined : finiteNumber(value.max)
    const step = value.step === undefined ? undefined : finiteNumber(value.step)
    if (placeholder === null || unit === null || min === null || max === null
        || step === null || (step !== undefined && step <= 0)) return null
    return {
      id,
      kind: "number",
      label,
      ...(description ? { description } : {}),
      ...(placeholder !== undefined ? { placeholder } : {}),
      ...(unit !== undefined ? { unit } : {}),
      ...(min !== undefined ? { min } : {}),
      ...(max !== undefined ? { max } : {}),
      ...(step !== undefined ? { step } : {}),
    }
  }

  if (value.kind === "text") {
    if (!hasOnlyKeys(value, [
      "id", "kind", "label", "description", "placeholder", "minLength", "maxLength",
    ])) return null
    const placeholder = optionalTrimmedText(value.placeholder, 120)
    const minLength = value.minLength === undefined
      ? undefined
      : boundedInteger(value.minLength, 0, 10_000)
    const maxLength = value.maxLength === undefined
      ? undefined
      : boundedInteger(value.maxLength, 1, 10_000)
    if (placeholder === null || minLength === null || maxLength === null) return null
    return {
      id,
      kind: "text",
      label,
      ...(description ? { description } : {}),
      ...(placeholder !== undefined ? { placeholder } : {}),
      ...(minLength !== undefined ? { minLength } : {}),
      ...(maxLength !== undefined ? { maxLength } : {}),
    }
  }

  return null
}

function parseControl(value: unknown): OrcaElicitationControlV1 | null {
  if (!isRecord(value)) return null
  const id = boundedId(value.id)
  const label = boundedText(value.label, 120)
  if (!id || !label) return null

  if (value.kind === "single_choice") {
    if (!hasOnlyKeys(value, ["id", "kind", "label", "options", "other"])
        || !Array.isArray(value.options)
        || value.options.length < 1
        || value.options.length > 20) return null
    const options = value.options.map(parseOption)
    if (options.some((option) => option === null)) return null
    let other: OrcaElicitationSingleChoiceControlV1["other"]
    if (value.other !== undefined) {
      if (!isRecord(value.other)
          || !hasOnlyKeys(value.other, ["label", "description", "field"])) return null
      const otherLabel = boundedText(value.other.label, 120)
      const otherDescription = value.other.description === undefined
        ? undefined
        : boundedText(value.other.description, 300)
      const field = parseField(value.other.field)
      if (!otherLabel || otherDescription === null || !field) return null
      other = {
        label: otherLabel,
        ...(otherDescription ? { description: otherDescription } : {}),
        field,
      }
    }
    return {
      id,
      kind: "single_choice",
      label,
      options: options as OrcaElicitationOptionV1[],
      ...(other ? { other } : {}),
    }
  }

  if (value.kind === "fields") {
    if (!hasOnlyKeys(value, ["id", "kind", "label", "fields"])
        || !Array.isArray(value.fields)
        || value.fields.length < 1
        || value.fields.length > 12) return null
    const fields = value.fields.map(parseField)
    if (fields.some((field) => field === null)) return null
    return { id, kind: "fields", label, fields: fields as OrcaElicitationFieldV1[] }
  }

  if (value.kind === "document") {
    if (!hasOnlyKeys(value, [
      "id", "kind", "label", "description", "allowedMimeTypes", "maxFiles",
    ])) return null
    const description = value.description === undefined
      ? undefined
      : boundedText(value.description, 300)
    const maxFiles = boundedInteger(value.maxFiles, 1, 10)
    if (description === null || maxFiles === null
        || !Array.isArray(value.allowedMimeTypes)
        || value.allowedMimeTypes.length > 12) return null
    const allowedMimeTypes = value.allowedMimeTypes.map((mimeType) => boundedText(mimeType, 120))
    if (allowedMimeTypes.some((mimeType) => mimeType === null)) return null
    return {
      id,
      kind: "document",
      label,
      ...(description ? { description } : {}),
      allowedMimeTypes: allowedMimeTypes as string[],
      maxFiles,
    }
  }

  if (value.kind === "approval") {
    if (!hasOnlyKeys(value, [
      "id", "kind", "label", "description", "approveLabel", "rejectLabel",
    ])) return null
    const description = value.description === undefined
      ? undefined
      : boundedText(value.description, 300)
    const approveLabel = boundedText(value.approveLabel, 52)
    const rejectLabel = boundedText(value.rejectLabel, 52)
    if (description === null || !approveLabel || !rejectLabel) return null
    return {
      id,
      kind: "approval",
      label,
      ...(description ? { description } : {}),
      approveLabel,
      rejectLabel,
    }
  }

  return null
}

function parseAlternative(value: unknown): OrcaElicitationAlternativeV1 | null {
  if (!isRecord(value) || !hasOnlyKeys(value, [
    "id", "kind", "label", "description", "credits", "requiresApproval",
  ])) return null
  const id = boundedId(value.id)
  const label = boundedText(value.label, 80)
  const description = value.description === undefined
    ? undefined
    : boundedText(value.description, 300)
  const credits = value.credits === undefined
    ? undefined
    : boundedInteger(value.credits, 0, 1_000_000)
  if (!id || !label || description === null || credits === null
      || !["research", "activate_agent", "ask_orca"].includes(String(value.kind))
      || typeof value.requiresApproval !== "boolean") return null
  return {
    id,
    kind: value.kind as OrcaElicitationAlternativeV1["kind"],
    label,
    ...(description ? { description } : {}),
    ...(credits !== undefined ? { credits } : {}),
    requiresApproval: value.requiresApproval,
  }
}

export function parseOrcaElicitationSpec(value: unknown): OrcaElicitationSpecV1 {
  if (!isRecord(value) || !hasOnlyKeys(value, [
    "schema", "requestId", "title", "description", "controls", "alternatives",
    "submitLabel", "cancelLabel", "notice", "cost", "sourceRevision",
  ]) || value.schema !== ORCA_ELICITATION_SCHEMA) {
    throw new Error("invalid_orca_elicitation_spec")
  }

  const requestId = boundedId(value.requestId)
  const title = boundedText(value.title, 160)
  const description = boundedText(value.description, 800)
  const submitLabel = boundedText(value.submitLabel, 52)
  const cancelLabel = value.cancelLabel === undefined
    ? undefined
    : boundedText(value.cancelLabel, 52)
  const notice = value.notice === undefined ? undefined : boundedText(value.notice, 800)
  const sourceRevision = boundedText(value.sourceRevision, 240)
  if (!requestId || !title || !description || !submitLabel || cancelLabel === null
      || notice === null || !sourceRevision || !Array.isArray(value.controls)
      || value.controls.length < 1 || value.controls.length > 12) {
    throw new Error("invalid_orca_elicitation_spec")
  }

  const controls = value.controls.map(parseControl)
  if (controls.some((control) => control === null)) {
    throw new Error("invalid_orca_elicitation_spec")
  }

  let alternatives: OrcaElicitationAlternativeV1[] | undefined
  if (value.alternatives !== undefined) {
    if (!Array.isArray(value.alternatives) || value.alternatives.length > 6) {
      throw new Error("invalid_orca_elicitation_spec")
    }
    const parsed = value.alternatives.map(parseAlternative)
    if (parsed.some((alternative) => alternative === null)) {
      throw new Error("invalid_orca_elicitation_spec")
    }
    alternatives = parsed as OrcaElicitationAlternativeV1[]
  }

  if (!isRecord(value.cost) || !hasOnlyKeys(value.cost, ["credits", "requiresApproval"])) {
    throw new Error("invalid_orca_elicitation_spec")
  }
  const credits = boundedInteger(value.cost.credits, 0, 1_000_000)
  if (credits === null || typeof value.cost.requiresApproval !== "boolean") {
    throw new Error("invalid_orca_elicitation_spec")
  }

  return {
    schema: ORCA_ELICITATION_SCHEMA,
    requestId,
    title,
    description,
    controls: controls as OrcaElicitationControlV1[],
    ...(alternatives !== undefined ? { alternatives } : {}),
    submitLabel,
    ...(cancelLabel ? { cancelLabel } : {}),
    ...(notice ? { notice } : {}),
    cost: { credits, requiresApproval: value.cost.requiresApproval },
    sourceRevision,
  }
}

export function parseReadableOrcaElicitationRequest(
  value: unknown,
): ReadableOrcaElicitationRequest {
  if (!isRecord(value) || !hasOnlyKeys(value, [
    "schema", "requestId", "origin", "handlerId", "status", "spec",
    "createdAt", "updatedAt", "expiresAt", "answeredAt", "result", "continuationState",
  ]) || value.schema !== ORCA_ELICITATION_SCHEMA
      || !("createdAt" in value) || !("updatedAt" in value)) {
    throw new Error("invalid_orca_elicitation_request")
  }
  const requestId = boundedId(value.requestId)
  const spec = parseOrcaElicitationSpec(value.spec)
  if (!requestId || spec.requestId !== requestId
      || (value.origin !== "directive" && value.origin !== "agent")
      || !isOrcaElicitationHandlerId(value.handlerId)
      || (value.status !== "pending" && value.status !== "answered")) {
    throw new Error("invalid_orca_elicitation_request")
  }
  let result: Record<string, unknown> | undefined
  if (value.result !== undefined) {
    if (!isRecord(value.result)) throw new Error("invalid_orca_elicitation_request")
    result = value.result
  }
  if ((value.status === "answered" && result === undefined)
      || (value.status === "pending" && result !== undefined)) {
    throw new Error("invalid_orca_elicitation_request")
  }
  let continuationState: ReadableAgentElicitationContinuationState | undefined
  if (value.continuationState !== undefined) {
    if (value.continuationState !== "awaiting_response"
        && value.continuationState !== "awaiting_continuation"
        && value.continuationState !== "resumed") {
      throw new Error("invalid_orca_elicitation_request")
    }
    continuationState = value.continuationState
  }
  if ((value.origin === "directive" && continuationState !== undefined)
      || (value.origin === "agent" && value.status === "pending"
        && continuationState !== "awaiting_response")
      || (value.origin === "agent" && value.status === "answered"
        && continuationState !== "awaiting_continuation"
        && continuationState !== "resumed")) {
    throw new Error("invalid_orca_elicitation_request")
  }
  return {
    schema: ORCA_ELICITATION_SCHEMA,
    requestId,
    origin: value.origin,
    handlerId: value.handlerId,
    status: value.status,
    spec,
    createdAt: value.createdAt,
    updatedAt: value.updatedAt,
    ...(value.expiresAt !== undefined ? { expiresAt: value.expiresAt } : {}),
    ...(value.answeredAt !== undefined ? { answeredAt: value.answeredAt } : {}),
    ...(result !== undefined ? { result } : {}),
    ...(continuationState !== undefined ? { continuationState } : {}),
  }
}

export function parseRecoverableAgentElicitationCheckpoint(
  value: unknown,
): RecoverableAgentElicitationCheckpoint {
  if (!isRecord(value) || !hasOnlyKeys(value, [
    "requestId", "origin", "handlerId", "status", "continuationState", "spec",
    "result", "createdAt", "updatedAt", "answeredAt",
  ]) || !("createdAt" in value) || !("updatedAt" in value)) {
    throw new Error("invalid_agent_elicitation_checkpoint")
  }
  const requestId = boundedId(value.requestId)
  const spec = parseOrcaElicitationSpec(value.spec)
  if (!requestId || spec.requestId !== requestId
      || value.origin !== "agent"
      || !isOrcaElicitationHandlerId(value.handlerId)
      || (value.status !== "pending" && value.status !== "answered")
      || (value.continuationState !== "awaiting_response"
        && value.continuationState !== "awaiting_continuation")) {
    throw new Error("invalid_agent_elicitation_checkpoint")
  }
  let result: Record<string, unknown> | undefined
  if (value.result !== undefined) {
    if (!isRecord(value.result)) throw new Error("invalid_agent_elicitation_checkpoint")
    result = value.result
  }
  if ((value.status === "pending"
        && (value.continuationState !== "awaiting_response" || result !== undefined))
      || (value.status === "answered"
        && value.continuationState !== "awaiting_continuation")) {
    throw new Error("invalid_agent_elicitation_checkpoint")
  }
  return {
    requestId,
    origin: "agent",
    handlerId: value.handlerId,
    status: value.status,
    continuationState: value.continuationState,
    spec,
    ...(result !== undefined ? { result } : {}),
    createdAt: value.createdAt,
    updatedAt: value.updatedAt,
    ...(value.answeredAt !== undefined ? { answeredAt: value.answeredAt } : {}),
  }
}

function parseFieldValue(value: unknown): string | number | boolean | null {
  if (typeof value === "string") {
    const normalized = value.trim()
    return normalized.length <= 10_000 ? normalized : null
  }
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "boolean") return value
  return null
}

function parseAnswer(value: unknown): OrcaElicitationAnswerV1 | null {
  if (!isRecord(value)) return null
  if (value.kind === "option") {
    if (!hasOnlyKeys(value, ["kind", "controlId", "optionId"])) return null
    const controlId = boundedId(value.controlId)
    const optionId = boundedId(value.optionId)
    return controlId && optionId ? { kind: "option", controlId, optionId } : null
  }
  if (value.kind === "other") {
    if (!hasOnlyKeys(value, ["kind", "controlId", "fieldId", "value"])) return null
    const controlId = boundedId(value.controlId)
    const fieldId = boundedId(value.fieldId)
    const fieldValue = parseFieldValue(value.value)
    return controlId && fieldId && fieldValue !== null
      ? { kind: "other", controlId, fieldId, value: fieldValue }
      : null
  }
  if (value.kind === "fields") {
    if (!hasOnlyKeys(value, ["kind", "controlId", "fields"])) return null
    const controlId = boundedId(value.controlId)
    if (!controlId || !isRecord(value.fields)) return null
    const entries = Object.entries(value.fields)
    if (entries.length < 1 || entries.length > 12) return null
    const fields: Record<string, string | number | boolean> = {}
    for (const [fieldId, rawValue] of entries) {
      const parsedId = boundedId(fieldId)
      const parsedValue = parseFieldValue(rawValue)
      if (!parsedId || parsedValue === null) return null
      fields[parsedId] = parsedValue
    }
    return { kind: "fields", controlId, fields }
  }
  if (value.kind === "approval") {
    if (!hasOnlyKeys(value, ["kind", "controlId", "approved"])) return null
    const controlId = boundedId(value.controlId)
    return controlId && typeof value.approved === "boolean"
      ? { kind: "approval", controlId, approved: value.approved }
      : null
  }
  if (value.kind === "alternative") {
    if (!hasOnlyKeys(value, ["kind", "alternativeId"])) return null
    const alternativeId = boundedId(value.alternativeId)
    return alternativeId ? { kind: "alternative", alternativeId } : null
  }
  return null
}

export function parseOrcaElicitationResponse(value: unknown): OrcaElicitationResponseV1 {
  if (!isRecord(value) || !hasOnlyKeys(value, [
    "schema", "requestId", "idempotencyKey", "answer",
  ]) || value.schema !== ORCA_ELICITATION_RESPONSE_SCHEMA) {
    throw new Error("invalid_orca_elicitation_response")
  }
  const requestId = boundedId(value.requestId)
  const idempotencyKey = boundedId(value.idempotencyKey)
  const answer = parseAnswer(value.answer)
  if (!requestId || !idempotencyKey || !answer) {
    throw new Error("invalid_orca_elicitation_response")
  }
  return {
    schema: ORCA_ELICITATION_RESPONSE_SCHEMA,
    requestId,
    idempotencyKey,
    answer,
  }
}

export function tryParseOrcaElicitationSpec(value: unknown): OrcaElicitationSpecV1 | null {
  try {
    return parseOrcaElicitationSpec(value)
  } catch {
    return null
  }
}

export function isOrcaElicitationHandlerId(value: unknown): value is OrcaElicitationHandlerId {
  return typeof value === "string"
    && (ORCA_ELICITATION_HANDLER_IDS as readonly string[]).includes(value)
}
